import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useFetchData } from 'services/api/useFetchData';
import { Division } from '../environments/EnvironmentTypes';
import { saveData } from 'services/api/saveData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Dropdown from 'components/forms/basefields/Dropdown';
import MultiSelect from 'components/forms/basefields/MultiSelect';
import DateInput from 'components/forms/basefields/DateInput';
import Checkbox from 'components/forms/basefields/Checkbox';
import { useModal } from 'components/modals/ModalContext';
import MigrationMailPreviewModal, { MigrationEmailType } from './MigrationMailPreviewModal';
import { fetchData } from 'services/api/fetchData';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { RoleType } from '../users/UserTypes';
import { JobScheme } from '../jobs/JobTypes';
import { LabelType } from '../settings/SettingsTypes';
import RoleForm from '../users/RoleForm';
import JobTypeForm from '../settings/jobs/JobTypeForm';
import LabelForm from '../settings/LabelForm';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import '../../style/scss/detailpage.scss';
import '../../style/scss/wizard.scss';
import '../../style/scss/import.scss';

// Set the endpoints in request order
const migration_endpoints = [
    'vat',
    'costcenter',
    'articlegroup',
    'material',
    'usergroup',
    'crafteruser',
    'typevehicle',
    'vehicle',
    'location',
    'contactperson',
    'client',
    'appointmenttypes',
    'workerevent',
    'projecttype',
    'project',
    'job',
    'assignedemployeeobject',
    'assignedemployeeobject_equipment',
    'timerow',
    'assignedemployeeobject_timerow',
    'attachment',
    'attachment_photo',
    'jobattachment',
    'jobmaterial',
    'task',
    'invoice2',
    'invoice2section',
    'invoice2line',
    'proposal2',
    'proposal2linegroup',
    'proposal2line',
    'order',
    'orderlinegroup',
    'orderline',
    'subscription',
    'subscriptionjobdetails',
    'subscriptiontask',
    'actiontype',
    'action',
    'note',
    'webform',
    'webformfield',
    'webformdata',
    'message'
];

type MigrationStep = 
  | 'currentImports'
  | 'login'
  | 'fetchBranches'
  | 'mapBranches'
  | 'fetchRoles'
  | 'mapRoles'
  | 'fetchWorktypes'
  | 'mapWorktypes'
  | 'importProgress';

interface LegacyEnvironment {
    'id': number;
    'legacy_environment_id': number;
    'legacy_environment_name': string;
    'legacy_import_id': number | null;
    'status': 'failed' | 'completed' | 'pending' | null;
    'imported_until': string | null;
    'large_import': boolean;
    'main_user_ids': number[];
    'main_user_emails': string[];
    'planned_migration_date': string | null;
    'announcement_sent_at': string | null;
    'golive_sent_at': string | null;
    'handover_sent_at': string | null;
    'line_dimensions_in_mm': boolean;
}

// Describes a user option for the main user dropdown
interface MainUserOption {
    'id': number;
    'email': string;
    'first_name': string;
    'last_name': string;
    'is_active': boolean;
}

// Describes an action that first needs a fresh legacy login
interface PendingLegacyAction {
    'type': 'restart' | 'mirror' | 'relink_projects' | 'client_references' | 'fix_user_dates';
    'legacyEnvObjectId': number;
    'legacyEnvironmentId': number;
    'legacyEnvironmentName': string;
}

// Describes the numbering report of one object type after a numbering sync
interface NumberingReport {
    'object_type': string;
    'inferred': boolean;
    'scheme': string | null;
    'scheme_changed': boolean;
    'next_number': number | null;
    'numbering_year': number | null;
    'samples': number;
    'renumbered': number;
}

// Describes the dry run report of a mirror run
interface MirrorReport {
    'envId': number;
    'additions_to_delete': { model: string, count: number }[];
    'imported_records_to_overwrite': { model: string, count: number }[];
}

const LegacyImportWizard: React.FC = () => {
    const { t } = useTranslation();
    const { activeFeatures, allowedRights, handleLogout } = useAuthContext();
    const { initializeModal } = useModal();
    const { response: fetchedDivisions } = useFetchData({ apiUrl: 'get_division_list', params: useMemo(() => ({ is_active: true }), [])});
    const { response: fetchedRoles } = useFetchData({ apiUrl: 'get_role_list', params: useMemo(() => ({ is_active: true }), [])});
    const { response: fetchedJobSchemes } = useFetchData({ apiUrl: 'get_workflow_for_feature_list/job', params: useMemo(() => ({ is_active: true }), [])});
    const { response: fetchedLabels } = useFetchData({ apiUrl: 'get_label_list', params: useMemo(() => ({ deleted: false }), [])});
    const { response: fetchedUsers } = useFetchData({ apiUrl: 'get_user_list', params: useMemo(() => ({ is_active: true }), [])});
    const [loadingEnvironments, setLoadingEnvironments] = useState<boolean>(true);
    const [importTagId, setImportTagId] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [tempPassword, setTempPassword] = useState('Temp7890');
    const [largeImport, setLargeImport] = useState<boolean>(false);
    const [legacyLoggedIn, setLegacyLoggedIn] = useState<boolean>(false);
    const [loginError, setLoginError] = useState<string | null>(null);
    const [pendingAction, setPendingAction] = useState<PendingLegacyAction | null>(null);
    const [mirrorReport, setMirrorReport] = useState<MirrorReport | null>(null);
    const [legacyEnvironmentId, setLegacyEnvironmentId] = useState<number | null>(null);
    const [legacyEnvironmentName, setLegacyEnvironmentName] = useState<string | null>(null);
    const [legacyEnvironments, setLegacyEnvironments] = useState<LegacyEnvironment[]>([]);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [roles, setRoles] = useState<RoleType[]>([]);
    const [jobSchemes, setJobSchemes] = useState<JobScheme[]>([]);
    const [labels, setLabels] = useState<LabelType[]>([]);
    const [legacyBranches, setLegacyBranches] = useState<{ id: number, name: string }[]>([]);
    const [legacyRoles, setLegacyRoles] = useState<{ name: string }[]>([]);
    const [legacyWorkTypes, setLegacyWorkTypes] = useState<{ id: number, name: string }[]>([]);
    const [branchMappings, setBranchMappings] = useState<{ [branchId: number]: number | null }>({});   
    const [roleMappings, setRoleMappings] = useState<{ [roleName: string]: number | null }>({});   
    const [workTypeMappings, setWorktypeMappings] = useState<{ [worktypeId: number]: { jobSchemeId?: number | null, labelId?: number | null } }>({});
    const [currentStep, setCurrentStep] = useState<MigrationStep>('currentImports');
    const [legacyImportId, setLegacyImportId] = useState<number | null>(null);
    const [defaultDivisionId, setDefaultDivisionId] = useState<number | null>(null);
    const [repairMessage, setRepairMessage] = useState<{ envId: number; text: string; values?: Record<string, any>; details?: string[] } | null>(null);
    const [checkPassword, setCheckPassword] = useState('Temp7890');
    const [environmentUsers, setEnvironmentUsers] = useState<MainUserOption[]>([]);
    const [mailMessage, setMailMessage] = useState<{ envId: number; text: string; values?: Record<string, any> } | null>(null);
    const [worktypeCreateMessage, setWorktypeCreateMessage] = useState<string | null>(null);
    const [endpointsProgress, setEndpointsProgress] = useState<{ legacy_model: string; status: string; progress: number, sub_steps?: { label: string; status: string; progress: number }[], error_message: string }[]>([]);
    const [buttonLoader, setButtonLoader] = useState<string | null>(null);

    // Get the existing imported legacy environments
    useEffect(() => {
        const fetchLegacyEnvironments = async () => {
            const response = await fetchData({ apiUrl: "get_imported_legacy_environments", handleLogout });

            // Save response
            const legacyEnvironments = response?.legacy_environments || [];
            setLegacyEnvironments(legacyEnvironments);
            setLoadingEnvironments(false);

            // Skip import overview if no legacy environments exist
            if (legacyEnvironments.length === 0) {
                setCurrentStep('login');
                return;
            }
        };
        fetchLegacyEnvironments();
    }, []);

    // Set the fetched divisions
    useEffect(() => {
        if (fetchedDivisions) setDivisions(fetchedDivisions.results);
    }, [fetchedDivisions]);

    // Set the fetched roles
    useEffect(() => {
        if (fetchedRoles) setRoles(fetchedRoles.results);
    }, [fetchedRoles]);

    // Set the fetched job schemes
    useEffect(() => {
        if (fetchedJobSchemes) setJobSchemes(fetchedJobSchemes.results);
    }, [fetchedJobSchemes]);

    // Set the fetched labels
    useEffect(() => {
        if (fetchedLabels) setLabels(fetchedLabels.results);
    }, [fetchedLabels]);

    // Set the fetched users for the main user dropdown
    useEffect(() => {
        if (fetchedUsers) setEnvironmentUsers(fetchedUsers.results);
    }, [fetchedUsers]);

    // Get the branches from the legacy software to set the mappings
    useEffect(() => {
        if (currentStep !== 'fetchBranches' || !legacyLoggedIn) return;

        const fetchLegacyBranches = (async () => {
            try {
                const response = await saveData({
                    apiUrl: 'get_legacy_branches',
                    method: 'post',
                    data: {}
                });

                // Save response
                const legacyBranches = response?.data.branches || [];
                setLegacyBranches(legacyBranches);

                // Initialize mapping
                const initialMappings: { [branchId: number]: number | null } = {};
                legacyBranches.forEach((branch: any) => {
                    initialMappings[branch.id] = null;
                });
                setBranchMappings(initialMappings);

                // Show the mapping step, also without branches to select the default division
                setCurrentStep('mapBranches');
            } catch (err) {
                console.error("Error fetching legacy branches:", err);
            }
        });
        fetchLegacyBranches();
    }, [legacyLoggedIn, currentStep]);

    // Get the roles from the legacy software to set the mappings
    useEffect(() => {
        if (currentStep !== 'fetchRoles' || !legacyLoggedIn) return;

        const fetchLegacyRoles = (async () => {
            try {
                const response = await saveData({
                    apiUrl: 'get_legacy_roles',
                    method: 'post',
                    data: {}
                });

                const legacyRoles = response?.data.roles || [];
                setLegacyRoles(legacyRoles);

                // Init mapping
                const initialMappings: { [roleName: string]: number | null } = {};
                legacyRoles.forEach((role: {id: number, name: string}) => {
                    initialMappings[role.name] = null;
                });
                setRoleMappings(initialMappings);
                setCurrentStep('mapRoles');
            } catch (err) {
                console.error("Error fetching legacy roles:", err);
            }
        });
        fetchLegacyRoles();
    }, [legacyLoggedIn, currentStep]);

    // Get the worktypes from the legacy software to set the mappings
    useEffect(() => {
        if (currentStep !== 'fetchWorktypes' || !legacyLoggedIn) return;

        const fetchLegacyWorkTypes = (async () => {
            try {
                const response = await saveData({
                    apiUrl: 'get_legacy_worktypes',
                    method: 'post',
                    data: {}
                });

                // Save response
                const legacyWorkTypes = response?.data.work_types || [];
                setLegacyWorkTypes(legacyWorkTypes);

                // Init mapping
                const mappings: { [worktypeId: number]: { jobSchemeId?: number | null, labelId?: number | null } } = {};
                legacyWorkTypes.forEach((workType: {id: number, name: string}) => {
                    mappings[workType.id] = { jobSchemeId: null, labelId: null };
                });
                setWorktypeMappings(mappings);
                setCurrentStep('mapWorktypes');
            } catch (err) {
                console.error("Error fetching legacy worktypes:", err);
            }
        });
        fetchLegacyWorkTypes();
    }, [legacyLoggedIn, currentStep]);

    // Fetch the import step progress periodically when the import is in progress
    useEffect(() => {
        if (!legacyImportId) return;

        const interval = setInterval(async () => {
            try {
                const response = await fetchData({
                    apiUrl: `get_legacy_import_status/${legacyImportId}`,
                    handleLogout
                });

                if (response?.steps) {
                    setEndpointsProgress(response.steps);

                    if (response?.status === 'completed' || response?.status === 'failed') {
                        clearInterval(interval);
                    }
                }
            } catch (err) {
                console.error("Error fetching import status:", err);
            }
        }, 2500);

        return () => clearInterval(interval);
    }, [legacyImportId]);

    // Restart an existing legacy import as a delta run
    const startDeltaImport = async (legacyEnvId: number, legacyEnvName: string) => {
        try {
            setButtonLoader('restart');

            const response = await saveData({
                apiUrl: 'start_legacy_import',
                method: 'post',
                data: {
                    legacy_environment_id: legacyEnvId,
                    legacy_environment_name: legacyEnvName,
                    default_user_password: tempPassword,
                    large_import: largeImport,
                }
            });

            if (response?.status === 202 && response?.data?.legacy_import_id) {
                setCurrentStep('importProgress');
                setLegacyImportId(response.data.legacy_import_id);
            }
        } catch (err) {
            console.error("Error starting import", err);
        } finally {
            setButtonLoader(null);
        }
    };

    // Run the mirror after the dry run report was confirmed
    const runMirror = async (legacyEnvObjectId: number) => {
        try {
            setButtonLoader('mirror');

            const response = await saveData({
                apiUrl: 'start_legacy_mirror',
                method: 'post',
                data: {
                    legacy_env_object_id: legacyEnvObjectId,
                    default_user_password: tempPassword,
                }
            });

            if (response?.status === 202 && response?.data?.legacy_import_id) {
                setCurrentStep('importProgress');
                setLegacyImportId(response.data.legacy_import_id);
            }
        } catch (err) {
            console.error("Error starting mirror", err);
        } finally {
            setButtonLoader(null);
        }
    };

    // Authenticate to the legacy system, the token is stored in the
    // session on the server and never reaches the client
    const authenticateLegacyImport = async () => {
        setButtonLoader('login');
        setLoginError(null);
        try {
            const response = await saveData({
                apiUrl: 'authenticate_legacy_import',
                method: 'post',
                data: {
                    username: username,
                    password: password,
                }
            });

            setLegacyLoggedIn(true);
            setLegacyEnvironmentId(response?.data.legacy_environment_id);
            setLegacyEnvironmentName(response?.data.legacy_environment_name);

            // Execute the pending restart or mirror after the fresh login
            if (pendingAction) {

                // Refuse a login of a different legacy environment
                if (String(response?.data.legacy_environment_id) !== String(pendingAction.legacyEnvironmentId)) {
                    setLoginError('legacy_migration.general.login_wrong_environment_message');
                    setButtonLoader(null);
                    return;
                }

                // Run the pending action and clear it
                if (pendingAction.type === 'restart') {
                    await startDeltaImport(pendingAction.legacyEnvironmentId, pendingAction.legacyEnvironmentName);
                } else if (pendingAction.type === 'relink_projects') {
                    await runRelinkProjects(pendingAction.legacyEnvObjectId);
                } else if (pendingAction.type === 'client_references') {
                    await runClientReferences(pendingAction.legacyEnvObjectId);
                } else if (pendingAction.type === 'fix_user_dates') {
                    await runFixUserDates(pendingAction.legacyEnvObjectId);
                } else {
                    await runMirror(pendingAction.legacyEnvObjectId);
                }
                setPendingAction(null);
                setButtonLoader(null);

                // Return to the environment overview so the started message of the action is visible
                if (pendingAction.type !== 'restart') {
                    setCurrentStep('currentImports');
                }
                return;
            }

            // Set the next step based on if the divisions feature is active
            activeFeatures.includes('divisions') ? (
                setCurrentStep('fetchBranches')
            ) : (
                setCurrentStep('fetchRoles')
            )
        } catch (err) {
            console.error("Failed to authenticate legacy import", err);
            setLoginError('legacy_migration.general.login_failed_message');
        }
        setButtonLoader(null);
    };

    // Start the legacy import
    const startImport = async () => {
        try {
            setButtonLoader('start');

            const response = await saveData({
                apiUrl: 'start_legacy_import',
                method: 'post',
                data: {
                    legacy_environment_id: legacyEnvironmentId,
                    legacy_environment_name: legacyEnvironmentName,
                    branch_to_division_map: branchMappings,
                    role_map: roleMappings,
                    worktype_map: workTypeMappings,
                    default_division_id: defaultDivisionId,
                    default_user_password: tempPassword,
                    large_import: largeImport,
                }
            });

            if (response?.status === 202 && response?.data?.legacy_import_id) {
                setCurrentStep('importProgress');
                setLegacyImportId(response.data.legacy_import_id);
            }
        } catch (err) {
            console.error("Error starting import", err);
        } finally {
            setButtonLoader(null);
        }
    };

    // Relink the imported invoices to their projects after the fresh login
    const runRelinkProjects = async (legacyEnvObjectId: number) => {
        setButtonLoader('relink_projects');
        setRepairMessage(null);
        try {
            await saveData({
                apiUrl: 'relink_legacy_invoice_projects',
                method: 'post',
                data: { legacy_env_object_id: legacyEnvObjectId }
            });

            // Show that the relink is started in the background
            setRepairMessage({
                envId: legacyEnvObjectId,
                text: 'legacy_migration.general.relink_projects_started_message'
            });
        } catch (err) {
            console.error("Error relinking invoice projects", err);
        } finally {
            setButtonLoader(null);
        }
    };

    // Set the references of the imported clients from the legacy platform after the fresh login
    const runClientReferences = async (legacyEnvObjectId: number) => {
        setButtonLoader('client_references');
        setRepairMessage(null);
        try {
            await saveData({
                apiUrl: 'import_legacy_client_references',
                method: 'post',
                data: { legacy_env_object_id: legacyEnvObjectId }
            });

            // Show that the import is started in the background
            setRepairMessage({
                envId: legacyEnvObjectId,
                text: 'legacy_migration.general.client_references_started_message'
            });
        } catch (err) {
            console.error("Error importing client references", err);
        } finally {
            setButtonLoader(null);
        }
    };

    // Convert the sizes of the imported document lines of a legacy environment to millimeters
    const runConvertDimensions = async (legacyEnvObjectId: number) => {
        setButtonLoader('convert_dimensions');
        setRepairMessage(null);
        try {
            const response = await saveData({
                apiUrl: 'convert_legacy_line_dimensions_mm',
                method: 'post',
                data: { legacy_env_object_id: legacyEnvObjectId }
            });

            // Show that the sizes are converted and reload the environments
            if (response?.status === 200) {
                setRepairMessage({
                    envId: legacyEnvObjectId,
                    text: 'legacy_migration.general.convert_dimensions_done_message'
                });
                setLegacyEnvironments(prev => prev.map(env => env.id === legacyEnvObjectId ? { ...env, line_dimensions_in_mm: true } : env));
            }
        } catch (err) {
            console.error("Error converting line sizes", err);
        } finally {
            setButtonLoader(null);
        }
    };

    // Take over the legacy created dates on the existing users after the fresh login
    const runFixUserDates = async (legacyEnvObjectId: number) => {
        setButtonLoader('fix_user_dates');
        setRepairMessage(null);
        try {
            await saveData({
                apiUrl: 'fix_legacy_user_created_dates',
                method: 'post',
                data: { legacy_env_object_id: legacyEnvObjectId }
            });

            // Show that the fix is started in the background
            setRepairMessage({
                envId: legacyEnvObjectId,
                text: 'legacy_migration.general.fix_user_dates_started_message'
            });
        } catch (err) {
            console.error("Error fixing user created dates", err);
        } finally {
            setButtonLoader(null);
        }
    };

    // Build the readable line of a numbering report of one object type
    const formatNumberingReport = (report: NumberingReport) => {
        const typeLabel = t(`legacy_migration.numbering_types.${report.object_type}`);
        const nextLabel = t('legacy_migration.general.sync_numbering_next_label');
        const notInferredLabel = t('legacy_migration.general.sync_numbering_not_inferred_label');
        const renumberedLabel = t('legacy_migration.general.sync_numbering_renumbered_label');
        const inferredPart = report.inferred ? '' : ` (${notInferredLabel})`;
        const renumberedPart = report.renumbered > 0 ? `, ${report.renumbered} ${renumberedLabel}` : '';
        return `${typeLabel}: ${report.scheme ?? '-'}, ${nextLabel} ${report.next_number ?? '-'}${renumberedPart}${inferredPart}`;
    };

    // Sync the numbering schemes and counters with the numbers in the database
    const runSyncNumbering = async (legacyEnvObjectId: number) => {
        setButtonLoader('sync_numbering');
        setRepairMessage(null);
        try {
            const response = await saveData({
                apiUrl: 'sync_legacy_numbering',
                method: 'post',
                data: { legacy_env_object_id: legacyEnvObjectId }
            });

            // Show the numbering per object type that was set
            const reports: NumberingReport[] = response?.data?.numbering ?? [];
            setRepairMessage({
                envId: legacyEnvObjectId,
                text: 'legacy_migration.general.sync_numbering_done_message',
                details: reports.map(formatNumberingReport)
            });
        } catch (err) {
            console.error("Error syncing numbering", err);
        } finally {
            setButtonLoader(null);
        }
    };

    // Open the login step for an action that needs a fresh legacy login
    const requestLegacyLogin = (type: PendingLegacyAction['type'], legacyEnv: LegacyEnvironment) => {
        setPendingAction({
            type: type,
            legacyEnvObjectId: legacyEnv.id,
            legacyEnvironmentId: legacyEnv.legacy_environment_id,
            legacyEnvironmentName: legacyEnv.legacy_environment_name,
        });
        setLargeImport(legacyEnv.large_import);
        setLoginError(null);
        setCurrentStep('login');
    };

    // Fetch the dry run report of a mirror run
    const fetchMirrorDryRun = async (legacyEnvObjectId: number) => {
        setButtonLoader('mirror_dry_run');
        setMirrorReport(null);
        try {
            const response = await saveData({
                apiUrl: 'start_legacy_mirror',
                method: 'post',
                data: {
                    legacy_env_object_id: legacyEnvObjectId,
                    dry_run: true,
                }
            });

            // Show the dry run report of this legacy environment
            setMirrorReport({
                envId: legacyEnvObjectId,
                additions_to_delete: response?.data?.additions_to_delete || [],
                imported_records_to_overwrite: response?.data?.imported_records_to_overwrite || [],
            });
        } catch (err) {
            console.error("Error fetching mirror dry run", err);
        } finally {
            setButtonLoader(null);
        }
    };

    // Check the temporary passwords of the imported users of a legacy environment
    const checkTemporaryPasswords = async (legacyEnvObjectId: number) => {
        setButtonLoader('check_passwords');
        setRepairMessage(null);
        try {
            const response = await saveData({
                apiUrl: 'check_legacy_temporary_passwords',
                method: 'post',
                data: { legacy_env_object_id: legacyEnvObjectId, default_user_password: checkPassword }
            });

            // Show the counts of the check with the emails of the special cases
            const report = response?.data || {};
            setRepairMessage({
                envId: legacyEnvObjectId,
                text: 'legacy_migration.general.check_passwords_result_message',
                values: {
                    imported: report.imported_users ?? 0,
                    temporary: report.with_temporary_password ?? 0,
                    own: report.with_own_password ?? 0,
                    reset: (report.reset_users || []).length,
                    resetEmails: (report.reset_users || []).join(', ') || '-',
                    inactiveEmails: (report.inactive_users || []).join(', ') || '-',
                    noOfficeEmails: (report.without_office_access || []).join(', ') || '-',
                }
            });
        } catch (err: any) {
            console.error("Error checking temporary passwords", err);

            // Show the validation error of the temporary password
            setRepairMessage({
                envId: legacyEnvObjectId,
                text: err?.response?.data?.detail || 'alert.general_save_error_message'
            });
        } finally {
            setButtonLoader(null);
        }
    };

    // Revert and delete the imported data
    const revertImport = async (legacyEnvObjectId: number) => {
        setButtonLoader('revert')
        setRepairMessage(null);
        try {
            await saveData({
                apiUrl: 'revert_legacy_import',
                method: 'post',
                data: { legacy_env_object_id: legacyEnvObjectId }
            });

            // Show that the deletion is started in the background
            setRepairMessage({
                envId: legacyEnvObjectId,
                text: 'legacy_migration.general.revert_started_message'
            });
        } catch (err) {
            console.error("Error reverting import", err);
        } finally {
            setButtonLoader(null);
        }
    };

    // Save the migration mail settings of a legacy environment
    const saveMigrationSettings = async (legacyEnvObjectId: number, data: { main_user_ids?: number[], planned_migration_date?: string | null }) => {
        try {
            const response = await saveData({
                apiUrl: `patch_legacy_migration_settings/${legacyEnvObjectId}`,
                method: 'patch',
                data: data
            });

            // Update the settings in the local legacy environments state
            setLegacyEnvironments(prev => prev.map(env => env.id === legacyEnvObjectId ? {
                ...env,
                main_user_ids: response?.data?.main_user_ids ?? env.main_user_ids,
                planned_migration_date: response?.data?.planned_migration_date ?? null,
            } : env));
        } catch (err: any) {
            console.error("Error saving migration settings", err);

            // Show the error detail of the backend
            setMailMessage({
                envId: legacyEnvObjectId,
                text: err?.response?.data?.detail || 'legacy_migration.general.mail_settings_failed_message'
            });
        }
    };

    // Store the sent timestamp locally after the preview modal sent the mail
    const handleMigrationMailSent = (legacyEnvObjectId: number, emailType: MigrationEmailType) => {
        const sentField = `${emailType}_sent_at` as const;
        setLegacyEnvironments(prev => prev.map(env => env.id === legacyEnvObjectId ? {
            ...env,
            [sentField]: new Date().toISOString(),
        } : env));

        // Show that the email is queued for sending
        setMailMessage({
            envId: legacyEnvObjectId,
            text: 'legacy_migration.general.mail_sent_message'
        });
    };

    // Fetch the preview of a migration email and open it in the email view modal
    const fetchMailPreview = async (legacyEnv: LegacyEnvironment, emailType: MigrationEmailType) => {
        setButtonLoader(`preview_${emailType}`);
        setMailMessage(null);
        try {
            const response = await fetchData({
                apiUrl: `get_legacy_migration_email_preview/${legacyEnv.id}/${emailType}`,
                handleLogout
            });

            // Determine if this mail was already sent before
            const alreadySent = Boolean(legacyEnv[`${emailType}_sent_at`]);

            // Open the email view modal with the preview of this migration email
            initializeModal(
                <MigrationMailPreviewModal
                    legacyEnvObjectId={legacyEnv.id}
                    emailType={emailType}
                    subject={response?.subject}
                    bodyHtml={response?.body_html}
                    recipients={response?.recipients}
                    alreadySent={alreadySent}
                    onSent={handleMigrationMailSent}
                />,
                { modalSize: 'medium', title: response?.subject }
            );
        } catch (err: any) {
            console.error("Error fetching mail preview", err);

            // Show the error detail of the backend
            setMailMessage({
                envId: legacyEnv.id,
                text: err?.response?.data?.detail || 'legacy_migration.general.mail_preview_failed_message'
            });
        } finally {
            setButtonLoader(null);
        }
    };

    // Open the role form to create a new role during the mapping,
    // the package feature check is skipped for this support only wizard
    const openCreateRoleModal = () => {
        initializeModal(
            <RoleForm ignorePackageFeature={true} />,
            {
                modalSize: 'medium',
                title: t('button.add_object_label', { object_name: t('role.general.object_name.singular') }),
                onSuccess: (newRole) => {
                    // Add the new role directly to the role dropdown options
                    if (newRole?.id) setRoles(prev => [...prev, newRole as RoleType]);
                }
            }
        );
    };

    // Open the job type form to create a new job workflow during the mapping
    const openCreateJobTypeModal = () => {
        initializeModal(
            <JobTypeForm />,
            {
                modalSize: 'medium',
                title: t('button.add_object_label', { object_name: t('job.job_type.general.object_name.singular') }),
                onSuccess: (newJobScheme) => {
                    // Add the new job type directly to the work type dropdown options
                    if (newJobScheme?.id) setJobSchemes(prev => [...prev, newJobScheme as JobScheme]);
                }
            }
        );
    };

    // Create a job workflow per legacy worktype in the chosen division and
    // map every worktype to its workflow, so the mapping is filled in one click
    const autoCreateWorktypeWorkflows = async () => {
        setButtonLoader('auto_create_worktypes');
        setWorktypeCreateMessage(null);
        try {
            const response = await saveData({
                apiUrl: 'create_legacy_worktype_workflows',
                method: 'post',
                data: { work_types: legacyWorkTypes, division_id: jobSchemeDivisionId }
            });

            // Replace the job type options with the refreshed list of the backend
            const workflows: JobScheme[] = response?.data?.workflows || [];
            setJobSchemes(workflows);

            // Select the created workflow for every worktype
            const worktypeWorkflowMap: { [worktypeId: string]: number } = response?.data?.worktype_workflow_map || {};
            setWorktypeMappings((prev) => {
                const next = { ...prev };
                Object.entries(worktypeWorkflowMap).forEach(([worktypeId, workflowId]) => {
                    next[Number(worktypeId)] = { ...next[Number(worktypeId)], jobSchemeId: workflowId };
                });
                return next;
            });
            setWorktypeCreateMessage(t('legacy_migration.general.auto_create_worktypes_message', { count: response?.data?.created_count ?? 0 }));
        } catch (err: any) {
            console.error("Error creating worktype workflows:", err);
            setWorktypeCreateMessage(err?.response?.data?.detail || 'Error');
        } finally {
            setButtonLoader(null);
        }
    };

    // Open the label form to create a new job label during the mapping
    const openCreateLabelModal = () => {
        initializeModal(
            <LabelForm />,
            {
                modalSize: 'medium',
                title: t('button.add_object_label', { object_name: t('label.general.object_name.singular') }),
                onSuccess: (newLabel) => {
                    // Add the new label directly to the label dropdown options
                    if (newLabel?.id) setLabels(prev => [...prev, newLabel as LabelType]);
                }
            }
        );
    };

    // Format a sent timestamp for the sent badges
    const formatSentAt = (sentAt: string | null) => {
        if (!sentAt) return '';
        return new Date(sentAt).toLocaleString('nl-NL', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Build the default planned migration date of two weeks ahead
    const defaultMigrationDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 14);
        return date.toISOString().split('T')[0];
    };

    // Determine the division to show the job types for, the chosen default
    // division, or otherwise the main division of the environment
    const mainDivisionId = divisions.find(division => division.is_main)?.id ?? null;
    const jobSchemeDivisionId = defaultDivisionId ?? mainDivisionId;

    // Determine if all roles are mapped
    const allRolesMapped = Object.values(roleMappings).every((val) => val !== null);

    // Determine if all worktypes are mapped
    const allWorktypesMapped = Object.values(workTypeMappings).every(
        ({ jobSchemeId, labelId }) => jobSchemeId !== null || labelId !== null
    );

    return (
        <div className='container-filled'>
            <div className='detailpage-header row'>
                <div className='header-title'>
                    <h2>{t('legacy_migration.general.legacy_migration_header')}</h2>
                </div>
            </div>
            <div className='wizard migration-wizard'>
                {currentStep === 'currentImports' && (
                    loadingEnvironments ? (
                        <div className="loader" />
                    ) : (
                        <>
                            <h3>{t('legacy_migration.general.imported_legacy_environment_header')}</h3>
                            {legacyEnvironments.map((legacyEnv, index) => (
                                <div className="card existing-legacy-import" key={index}>
                                    <div className='existing-legacy-import-header'>
                                        <h4>{legacyEnv.legacy_environment_name}</h4>
                                        <div className={`existing-legacy-import-status ${legacyEnv.status ? legacyEnv.status : 'failed' }`}>
                                            {legacyEnv.status ? t(`legacy_migration.status.${legacyEnv.status}`) : 'Mislukt'}
                                        </div>
                                    </div>
                                    {legacyEnv.status === 'completed' && (
                                        <p>
                                            Oude data geïmporteerd tot {legacyEnv.imported_until ? new Date(legacyEnv.imported_until).toLocaleString('nl-NL', {
                                                day: 'numeric',
                                                month: 'long',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            }) : ''}
                                        </p>
                                    )}
                                    {legacyEnv.status === 'failed' && (
                                        <p>{t('legacy_migration.general.import_failed_label')}</p>
                                    )}
                                    {legacyEnv.status === 'pending' && (
                                        <p>{t('legacy_migration.general.import_in_progress_label')}</p>
                                    )}
                                    <div className='container-space-between'>
                                        <div className='existing-legacy-import-buttons'>
                                            <SecondaryButton
                                                onClick={() => {
                                                    setCurrentStep('importProgress');
                                                    setLegacyImportId(legacyEnv.legacy_import_id)
                                                }}
                                                label="legacy_migration.general.view_status_button_label"
                                                size="small"
                                                loading={buttonLoader === 'start'}
                                            />
                                            <SecondaryButton
                                                onClick={() => requestLegacyLogin('restart', legacyEnv)}
                                                label={legacyEnv.status === 'completed' ? 'legacy_migration.general.fetch_changes_button_label' : 'legacy_migration.general.restart_import_button_label'}
                                                size="small"
                                                loading={buttonLoader === 'restart'}
                                            />
                                            {legacyEnv.status === 'completed' && (
                                                <SecondaryButton
                                                    onClick={() => fetchMirrorDryRun(legacyEnv.id)}
                                                    label='legacy_migration.general.mirror_button_label'
                                                    disabled={{
                                                        disabled: !allowedRights.includes('no_user'),
                                                        message: 'legacy_migration.general.support_only_action_message'
                                                    }}
                                                    size="small"
                                                    loading={buttonLoader === 'mirror_dry_run'}
                                                />
                                            )}
                                            {legacyEnv.status === 'completed' && (
                                                <SecondaryButton
                                                    onClick={() => requestLegacyLogin('client_references', legacyEnv)}
                                                    label='legacy_migration.general.client_references_button_label'
                                                    tooltipText='legacy_migration.general.client_references_tooltipText'
                                                    disabled={{
                                                        disabled: !allowedRights.includes('no_user'),
                                                        message: 'legacy_migration.general.support_only_action_message'
                                                    }}
                                                    size="small"
                                                    loading={buttonLoader === 'client_references'}
                                                />
                                            )}
                                            {legacyEnv.status === 'completed' && !legacyEnv.line_dimensions_in_mm && (
                                                <SecondaryButton
                                                    onClick={() => runConvertDimensions(legacyEnv.id)}
                                                    label='legacy_migration.general.convert_dimensions_button_label'
                                                    tooltipText='legacy_migration.general.convert_dimensions_tooltipText'
                                                    disabled={{
                                                        disabled: !allowedRights.includes('no_user'),
                                                        message: 'legacy_migration.general.support_only_action_message'
                                                    }}
                                                    size="small"
                                                    loading={buttonLoader === 'convert_dimensions'}
                                                />
                                            )}
                                            {legacyEnv.status === 'completed' && (
                                                <SecondaryButton
                                                    onClick={() => requestLegacyLogin('relink_projects', legacyEnv)}
                                                    label='legacy_migration.general.relink_projects_button_label'
                                                    tooltipText='legacy_migration.general.relink_projects_tooltipText'
                                                    disabled={{
                                                        disabled: !allowedRights.includes('no_user'),
                                                        message: 'legacy_migration.general.support_only_action_message'
                                                    }}
                                                    size="small"
                                                    loading={buttonLoader === 'relink_projects'}
                                                />
                                            )}
                                            {legacyEnv.status === 'completed' && (
                                                <SecondaryButton
                                                    onClick={() => requestLegacyLogin('fix_user_dates', legacyEnv)}
                                                    label='legacy_migration.general.fix_user_dates_button_label'
                                                    tooltipText='legacy_migration.general.fix_user_dates_tooltipText'
                                                    disabled={{
                                                        disabled: !allowedRights.includes('no_user'),
                                                        message: 'legacy_migration.general.support_only_action_message'
                                                    }}
                                                    size="small"
                                                    loading={buttonLoader === 'fix_user_dates'}
                                                />
                                            )}
                                            {legacyEnv.status === 'completed' && (
                                                <SecondaryButton
                                                    onClick={() => runSyncNumbering(legacyEnv.id)}
                                                    label='legacy_migration.general.sync_numbering_button_label'
                                                    tooltipText='legacy_migration.general.sync_numbering_tooltipText'
                                                    disabled={{
                                                        disabled: !allowedRights.includes('no_user'),
                                                        message: 'legacy_migration.general.support_only_action_message'
                                                    }}
                                                    size="small"
                                                    loading={buttonLoader === 'sync_numbering'}
                                                />
                                            )}
                                        </div>
                                        {legacyEnv.status === 'completed' && (
                                            <div className='existing-legacy-import-buttons'>
                                                <input
                                                    type="text"
                                                    placeholder={t('legacy_migration.general.temp_password_label')}
                                                    value={checkPassword}
                                                    onChange={(e) => setCheckPassword(e.target.value)}
                                                />
                                                <SecondaryButton
                                                    onClick={() => checkTemporaryPasswords(legacyEnv.id)}
                                                    label='legacy_migration.general.check_passwords_button_label'
                                                    tooltipText='legacy_migration.general.check_passwords_tooltipText'
                                                    disabled={{
                                                        disabled: !allowedRights.includes('no_user'),
                                                        message: 'legacy_migration.general.support_only_action_message'
                                                    }}
                                                    size="small"
                                                    loading={buttonLoader === 'check_passwords'}
                                                />
                                            </div>
                                        )}
                                        <SecondaryButton
                                            onClick={() => revertImport(legacyEnv.id)}
                                            label='legacy_migration.general.delete_imported_data_button_label'
                                            disabled={{
                                                disabled: !allowedRights.includes('no_user'),
                                                message: 'legacy_migration.general.support_only_revert_message'
                                            }}
                                            size="small"
                                            customClass='danger'
                                            loading={buttonLoader === 'revert'}
                                        />
                                    </div>
                                    {repairMessage?.envId === legacyEnv.id && (
                                        <div className='repair-message'>
                                            <p>{t(repairMessage.text, repairMessage.values)}</p>
                                            {repairMessage.details && repairMessage.details.length > 0 && (
                                                <ul className='repair-message-details'>
                                                    {repairMessage.details.map((detail, detailIndex) => (
                                                        <li key={detailIndex}>{detail}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    )}
                                    {/* Temporary without the completed status check so the mails can be tested on staging */}
                                    {allowedRights.includes('no_user') && (
                                        <div className='migration-mail-section'>
                                            <h5>{t('legacy_migration.general.migration_mails_header')}</h5>
                                            <div className='migration-mail-settings'>
                                                <div className='migration-mail-setting'>
                                                    <label>{t('legacy_migration.general.main_user_label')}</label>
                                                    <MultiSelect<MainUserOption>
                                                        options={environmentUsers.filter(user => user.is_active)}
                                                        id={`main_user_${legacyEnv.id}`}
                                                        name={`main_user_${legacyEnv.id}`}
                                                        disabled_selected={t('forms.select_type_disabled_selected')}
                                                        selectedOption={environmentUsers.filter(user => (legacyEnv.main_user_ids || []).includes(user.id))}
                                                        onChange={(selectedValue) => {
                                                            // Toggle the clicked user in the main users selection
                                                            const currentIds = legacyEnv.main_user_ids || [];
                                                            if (selectedValue === 'none') {
                                                                saveMigrationSettings(legacyEnv.id, { main_user_ids: [] });
                                                                return;
                                                            }
                                                            const userId = parseInt(selectedValue);
                                                            const newIds = currentIds.includes(userId)
                                                                ? currentIds.filter(id => id !== userId)
                                                                : [...currentIds, userId];
                                                            saveMigrationSettings(legacyEnv.id, { main_user_ids: newIds });
                                                        }}
                                                        selectionFormat={(option) => `${option.first_name} ${option.last_name}`}
                                                        optionFormat={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
                                                        showSearch={false}
                                                        allowNoneOption={true}
                                                    />
                                                </div>
                                                <div className='migration-mail-setting'>
                                                    <label>{t('legacy_migration.general.planned_migration_date_label')}</label>
                                                    <DateInput
                                                        id={`planned_migration_date_${legacyEnv.id}`}
                                                        name={`planned_migration_date_${legacyEnv.id}`}
                                                        localDatetime={legacyEnv.planned_migration_date || defaultMigrationDate()}
                                                        onChange={(date) => {
                                                            saveMigrationSettings(legacyEnv.id, {
                                                                planned_migration_date: date
                                                            });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className='migration-mail-rows'>
                                                <div className='migration-mail-row'>
                                                    <div className='migration-mail-row-label'>
                                                        {t('legacy_migration.general.announcement_mail_label')}
                                                        {legacyEnv.announcement_sent_at && (
                                                            <span className='migration-mail-sent-badge'>
                                                                {t('legacy_migration.general.mail_sent_at_label')} {formatSentAt(legacyEnv.announcement_sent_at)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <SecondaryButton
                                                        onClick={async () => {
                                                            if (!legacyEnv.planned_migration_date) {
                                                                await saveMigrationSettings(legacyEnv.id, { planned_migration_date: defaultMigrationDate() });
                                                            }
                                                            fetchMailPreview(legacyEnv, 'announcement');
                                                        }}
                                                        label='legacy_migration.general.mail_preview_button_label'
                                                        size="small"
                                                        loading={buttonLoader === 'preview_announcement'}
                                                    />
                                                </div>
                                                <div className='migration-mail-row'>
                                                    <div className='migration-mail-row-label'>
                                                        {t('legacy_migration.general.golive_mail_label')}
                                                        {legacyEnv.golive_sent_at && (
                                                            <span className='migration-mail-sent-badge'>
                                                                {t('legacy_migration.general.mail_sent_at_label')} {formatSentAt(legacyEnv.golive_sent_at)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <SecondaryButton
                                                        onClick={() => fetchMailPreview(legacyEnv, 'golive')}
                                                        label='legacy_migration.general.mail_preview_button_label'
                                                        size="small"
                                                        loading={buttonLoader === 'preview_golive'}
                                                    />
                                                </div>
                                                <div className='migration-mail-row'>
                                                    <div className='migration-mail-row-label'>
                                                        {t('legacy_migration.general.handover_mail_label')}
                                                        {legacyEnv.handover_sent_at && (
                                                            <span className='migration-mail-sent-badge'>
                                                                {t('legacy_migration.general.mail_sent_at_label')} {formatSentAt(legacyEnv.handover_sent_at)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <SecondaryButton
                                                        onClick={() => fetchMailPreview(legacyEnv, 'handover')}
                                                        label='legacy_migration.general.mail_preview_button_label'
                                                        size="small"
                                                        loading={buttonLoader === 'preview_handover'}
                                                    />
                                                </div>
                                            </div>
                                            {mailMessage?.envId === legacyEnv.id && (
                                                <p className='repair-message'>{t(mailMessage.text)}</p>
                                            )}
                                        </div>
                                    )}
                                    {mirrorReport?.envId === legacyEnv.id && (
                                        <div className='mirror-dry-run-report'>
                                            <h5>{t('legacy_migration.general.mirror_dry_run_header')}</h5>
                                            {mirrorReport.additions_to_delete.length === 0 && mirrorReport.imported_records_to_overwrite.length === 0 ? (
                                                <p>{t('legacy_migration.general.mirror_nothing_found_label')}</p>
                                            ) : (
                                                <>
                                                    {mirrorReport.additions_to_delete.length > 0 && (
                                                        <>
                                                            <p>{t('legacy_migration.general.mirror_additions_label')}:</p>
                                                            <ul>
                                                                {mirrorReport.additions_to_delete.map((entry) => (
                                                                    <li key={entry.model}>{entry.model}: {entry.count}</li>
                                                                ))}
                                                            </ul>
                                                        </>
                                                    )}
                                                    {mirrorReport.imported_records_to_overwrite.length > 0 && (
                                                        <>
                                                            <p>{t('legacy_migration.general.mirror_overwrites_label')}:</p>
                                                            <ul>
                                                                {mirrorReport.imported_records_to_overwrite.map((entry) => (
                                                                    <li key={entry.model}>{entry.model}: {entry.count}</li>
                                                                ))}
                                                            </ul>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                            <p>{t('legacy_migration.general.mirror_deletions_note')}</p>
                                            <PrimaryButton
                                                onClick={() => requestLegacyLogin('mirror', legacyEnv)}
                                                label='legacy_migration.general.mirror_confirm_button_label'
                                                size="small"
                                                loading={buttonLoader === 'mirror'}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                            {activeFeatures.includes('divisions') && (
                                <div className="add-another-environment-button"
                                     onClick={() => {setCurrentStep('login')}}>
                                    + Data inlezen vanuit een andere omgeving uit het oude platform
                                </div>
                            )}
                        </>
                    )
                )} 

                {currentStep === 'login' && (
                    <div className='authentication'>
                        <div className='section-header'>
                            <h3>
                                {t('legacy_migration.general.authentication_header')}
                                {pendingAction ? ` - ${pendingAction.legacyEnvironmentName}` : ''}
                            </h3>
                            <p>
                                {pendingAction ? (
                                    t('legacy_migration.general.login_for_action_explanationText')
                                ) : (
                                    t('legacy_migration.general.authentication_explanationText')
                                )}
                            </p>
                        </div>
                        <div className='authentication-form'>
                            <input
                                type="text"
                                placeholder={t('legacy_migration.general.username_label')}
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                            <input
                                type="password"
                                placeholder={t('legacy_migration.general.password_label')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <label htmlFor="temp_password">
                                {t('legacy_migration.general.temp_password_label')}
                            </label>
                            <input
                                id="temp_password"
                                type="text"
                                placeholder={t('legacy_migration.general.temp_password_label')}
                                value={tempPassword}
                                onChange={(e) => setTempPassword(e.target.value)}
                            />
                            <p>{t('legacy_migration.general.temp_password_explanationText')}</p>
                            <Checkbox
                                name="large_import"
                                label="legacy_migration.general.large_import_label"
                                align="left"
                                value={largeImport}
                                onChange={(e) => setLargeImport(e.target.checked)}
                            />
                            <p>{t('legacy_migration.general.large_import_explanationText')}</p>
                            <PrimaryButton
                                onClick={() => authenticateLegacyImport()}
                                label="legacy_migration.general.login_button_text"
                                size="medium"
                                loading={buttonLoader === 'login'}
                            />
                            {loginError && (
                                <p className='repair-message'>{t(loginError)}</p>
                            )}
                        </div>
                    </div>
                )}


                {(currentStep === 'fetchBranches' || currentStep === 'mapBranches') && (
                    <div className='mapping-section'>
                        <div className='section-header'>
                            <h3>
                                {t('legacy_migration.general.branches_header')}
                            </h3>
                            <p>
                                {t('legacy_migration.general.branches_explanationText')}
                            </p>
                        </div>
                        {currentStep === 'fetchBranches' && (
                            <p>Bezig met ophalen van de oude vestigingen...</p>
                        )}
                        {currentStep === 'mapBranches' && (
                            <>
                                <div className="mapping-fields">
                                    <div className="mapping-row">
                                        <p>Standaard vestiging (voor niet toegewezen data)</p>
                                        <Dropdown<Division>
                                            options={divisions.filter(division => division.is_active)}
                                            id="default_division"
                                            name="default_division"
                                            disabled_selected={t('forms.select_type_disabled_selected')}
                                            selectedOption={divisions.find(division => division.id === defaultDivisionId) || undefined}
                                            onChange={(selectedValue) => {
                                                setDefaultDivisionId(selectedValue ? parseInt(selectedValue) : null);
                                            }}
                                            selectionFormat={(option) => `${option.name}`}
                                            optionFormat="name"
                                            showSearch={false}
                                            allowNoneOption={true}
                                        />
                                        <div />
                                    </div>
                                    {legacyBranches.map((branch) => (
                                        <div key={branch.id} className="mapping-row">
                                            <p>{branch.name || `Vestiging ${branch.id}`}</p>
                                            <Dropdown<Division>
                                                options={divisions.filter(division => division.is_active)}
                                                id={`branch_${branch.id}`}
                                                name={`branch_${branch.id}`}
                                                disabled_selected={t('forms.select_type_disabled_selected')}
                                                selectedOption={divisions.find(division => division.id === branchMappings[branch.id]) || undefined}
                                                onChange={(selectedValue) => {
                                                    setBranchMappings(prev => ({
                                                        ...prev,
                                                        [branch.id]: selectedValue ? parseInt(selectedValue) : null
                                                    }));
                                                }}
                                                selectionFormat={(option) => `${option.name}`}
                                                optionFormat="name"
                                                showSearch={false}
                                                allowNoneOption={true}
                                            />
                                            <div />
                                        </div>
                                    ))}
                                    
                                </div>
                                <div className='container-space-between button-row'>
                                    <div></div>
                                    <PrimaryButton 
                                        onClick={() => setCurrentStep('fetchRoles')}  
                                        label="general.next"
                                        size="medium"
                                    />
                                </div>
                                
                            </>
                        )}
                    </div>
                )}

                {(currentStep === 'fetchRoles' || currentStep === 'mapRoles') && (
                    <div className='mapping-section'>
                        <div className='section-header'>
                            <h3>
                                {t('legacy_migration.general.roles_header')}
                            </h3>
                            <p>
                                {t('legacy_migration.general.roles_explanationText')}
                            </p>
                        </div>
                        {currentStep === 'fetchRoles' && (
                            <p>Bezig met ophalen van de oude gebruikersrollen...</p>
                        )}
                        {currentStep === 'mapRoles' && (
                            <>
                                <div className="mapping-fields">
                                    {legacyRoles.map((legacyRole) => (
                                        <div key={legacyRole.name} className="mapping-row">
                                            <p>{t(`legacy_migration.roles.${legacyRole.name}`)}</p>
                                            <Dropdown<RoleType>
                                                options={roles.filter(role => role.is_active)}
                                                id={`role_${legacyRole.name}`}
                                                name={`role_${legacyRole.name}`}
                                                disabled_selected={t('forms.select_type_disabled_selected')}
                                                selectedOption={roles.find(role => role.id === roleMappings[legacyRole.name]) || undefined}
                                                onChange={(selectedValue) => {
                                                    setRoleMappings(prev => ({
                                                        ...prev,
                                                        [legacyRole.name]: selectedValue ? parseInt(selectedValue) : null
                                                    }));
                                                }}
                                                selectionFormat={(option) => `${option.name}`}
                                                optionFormat="name"
                                                showSearch={false}
                                                allowNoneOption={true}
                                            />
                                            <div />
                                        </div>
                                    ))}
                                </div>
                                <div className='container-space-between button-row'>
                                    <div className='button-row-left'>
                                        <SecondaryButton
                                            onClick={() => openCreateRoleModal()}
                                            label='legacy_migration.general.create_role_button_label'
                                            size="medium"
                                        />
                                        {!allRolesMapped && <p>{t('legacy_migration.general.assign_all_items_message')}</p>}
                                    </div>
                                    <PrimaryButton
                                        onClick={() => setCurrentStep('fetchWorktypes')}
                                        disabled={{
                                            disabled: !allRolesMapped,
                                            message: 'legacy_migration.general.assign_all_items_message'
                                        }}
                                        label="general.next"
                                        size="medium"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}

                {(currentStep === 'fetchWorktypes' || currentStep === 'mapWorktypes') && (
                    <div className='mapping-section'>
                        <div className='section-header'>
                            <h3>
                                {t('legacy_migration.general.worktypes_header')}
                            </h3>
                            <p>
                                {t('legacy_migration.general.worktypes_explanationText')}
                            </p>
                        </div>
                        {currentStep === 'fetchWorktypes' && (
                            <p>Bezig met downloaden type diensten...</p>
                        )}
                        {currentStep === 'mapWorktypes' && (
                            <>
                                <div className="mapping-fields">
                                    <div className="mapping-row-three">
                                        <h6>Type dienst (oud)</h6>
                                        <h6>Werkopdracht type</h6>
                                        <h6>Label</h6>
                                    </div>
                                    {legacyWorkTypes.map((worktype) => (
                                        <div key={worktype.id} className="mapping-row-three">
                                            <p>{worktype.name}</p>
                                            <Dropdown<JobScheme>
                                                options={jobSchemes.filter(jobScheme => jobScheme.is_active && jobScheme.division === jobSchemeDivisionId)}
                                                id={`job_scheme_${worktype.id}`}
                                                name={`job_scheme_${worktype.id}`}
                                                disabled_selected={t('forms.select_type_disabled_selected')}
                                                selectedOption={jobSchemes.find(jobScheme => jobScheme.id === workTypeMappings[worktype.id]?.jobSchemeId)}
                                                onChange={(selectedValue) => {
                                                    setWorktypeMappings((prev) => ({
                                                        ...prev,
                                                        [worktype.id]: {
                                                            ...prev[worktype.id],
                                                            jobSchemeId: selectedValue ? parseInt(selectedValue) : null
                                                        }
                                                    }));
                                                }}
                                                selectionFormat={(option) => `${option.name}`}
                                                optionFormat="name"
                                                showSearch={false}
                                                allowNoneOption={false}
                                            />
                                            <Dropdown<LabelType>
                                                options={labels.filter(label => !label.deleted && label.connected_feature?.toLowerCase() === 'jobs')}
                                                id={`label_${worktype.id}`}
                                                name={`label_${worktype.id}`}
                                                disabled_selected={t('forms.select_type_disabled_selected')}
                                                selectedOption={labels.find(label => label.id === workTypeMappings[worktype.id]?.labelId)}
                                                onChange={(selectedValue) => {
                                                    setWorktypeMappings((prev) => ({
                                                        ...prev,
                                                        [worktype.id]: {
                                                            ...prev[worktype.id],
                                                            labelId: selectedValue ? parseInt(selectedValue) : null
                                                        }
                                                    }));
                                                }}
                                                selectionFormat={(option) => `${option.name}`}
                                                optionFormat="name"
                                                optionMode="label"
                                                showSearch={false}
                                                allowNoneOption={true}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className='container-space-between button-row'>
                                    <div className='button-row-left'>
                                        <SecondaryButton
                                            onClick={() => autoCreateWorktypeWorkflows()}
                                            label='legacy_migration.general.auto_create_worktypes_button_label'
                                            size="medium"
                                            loading={buttonLoader === 'auto_create_worktypes'}
                                        />
                                        <SecondaryButton
                                            onClick={() => openCreateJobTypeModal()}
                                            label='legacy_migration.general.create_worktype_button_label'
                                            size="medium"
                                        />
                                        <SecondaryButton
                                            onClick={() => openCreateLabelModal()}
                                            label='legacy_migration.general.create_label_button_label'
                                            size="medium"
                                        />
                                        {worktypeCreateMessage && <p>{worktypeCreateMessage}</p>}
                                        {!allWorktypesMapped && <p>{t('legacy_migration.general.assign_all_items_message')}</p>}
                                    </div>
                                    <PrimaryButton
                                        onClick={() => {
                                            startImport();
                                            setCurrentStep('importProgress');
                                        }}
                                        disabled={{
                                            disabled: !allWorktypesMapped,
                                            message: 'legacy_migration.general.assign_all_items_message'
                                        }}
                                        label="legacy_migration.general.start_import"
                                        size="medium"
                                        loading={buttonLoader === 'start'}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}

                {currentStep === 'importProgress' && (
                    <div className='endpoint-section'>
                        <div className='section-header'>
                            <h3>
                                {t('legacy_migration.general.import_started_header')}
                            </h3>
                            <p>
                                {t('legacy_migration.general.import_started_explanationText')}
                            </p>
                        </div>
                        {[
                            ...migration_endpoints,
                            // Also show steps that are not in the fixed list, such as repair steps
                            ...endpointsProgress
                                .map(endpoint => endpoint.legacy_model)
                                .filter(legacyModel => !migration_endpoints.includes(legacyModel)),
                        ].map((endpointName) => {
                            const step = endpointsProgress.find(endpoint => endpoint.legacy_model === endpointName);
                            const progress = step?.progress ?? 0;
                            const status = step?.status ?? 'pending';

                            return (
                                <div key={endpointName}>
                                    <div className={`endpoint-item ${status}`}>
                                        <div className='endpoint-icon'>
                                            {status === 'completed' && (
                                                <FontAwesomeIcon 
                                                    icon={faCheck} 
                                                    className='done' 
                                                />
                                            )}
                                            {status === 'failed' && (
                                                <FontAwesomeIcon 
                                                    icon={faTimes} 
                                                    className='error' 
                                                />
                                            )}
                                            {(status === 'downloading' || status === 'in_progress') && (
                                                <div className="loader" />
                                            )}
                                        </div>
                                        <div>{t(`legacy_migration.endpoints.${endpointName}`)}</div>
                                        <div className='endpoint-status'>
                                            {status === 'downloading' && 'Bezig met downloaden...'}
                                            {status === 'in_progress' && `Bezig met importeren... ${progress}%`}
                                            {status === 'failed' && 'Mislukt'}
                                            {status === 'completed' && 'Voltooid'}
                                            {status === 'pending' && 'In afwachting'}
                                        </div>
                                    </div>
                                    {(step?.sub_steps?.length ?? 0) > 1 && (
                                        <div className='endpoint-sub-items'>
                                            {step?.sub_steps?.map((subStep) => (
                                                <div key={subStep.label} className={`endpoint-sub-item ${subStep.status}`}>
                                                    <div className='endpoint-sub-label'>{subStep.label}</div>
                                                    <div className='endpoint-sub-status'>
                                                        {subStep.status === 'downloading' && 'Bezig met downloaden...'}
                                                        {subStep.status === 'in_progress' && `Bezig met importeren... ${subStep.progress}%`}
                                                        {subStep.status === 'failed' && 'Mislukt'}
                                                        {subStep.status === 'completed' && 'Voltooid'}
                                                        {subStep.status === 'pending' && 'In afwachting'}
                                                    </div>
                                                    <div className='sub-progress-bar'>
                                                        <div
                                                            className='sub-progress-fill'
                                                            style={{ width: `${subStep.status === 'completed' ? 100 : subStep.progress}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <p>
                                        {step?.error_message}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LegacyImportWizard;